import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { generateHash } from 'helpers/generateHash';
import { Toast, ToastType } from './Toast';
import { ToastsWrapper } from './styles';

const ToastContext = React.createContext<IToastContext>({} as IToastContext);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = React.useState<IToastInfo[]>([]);

  /**
   * metodo para dar suporte a usos com a lib antiga
   */
  const addToast = (params: { type: ToastType; description: string }) => showToast(params.type, params.description);

  const showToast = (type: ToastType, message: string, delay = 5000) => {
    const toastInfo = {
      id: generateHash(),
      type,
      message,
      delay,
    };

    setToasts((prevState) => {
      const newState = [...prevState];

      const toastActiveIndex = newState.findIndex((toast) => {
        return toast.type === type && toast.message === message;
      });

      if (toastActiveIndex !== -1) {
        newState[toastActiveIndex] = toastInfo;
      } else {
        newState.push(toastInfo);
      }

      return newState;
    });
  };

  const close = (id: string) => {
    setToasts((prevState) => prevState.filter((toast) => toast.id !== id));
  };

  const value = React.useMemo(() => ({ showToast, addToast }), []);

  useEffect(() => {
    const callbackShowToastFromEvent = (e) => {
      const { type, message, delay } = e.detail;
      showToast(type, message, delay);
    };

    window.addEventListener('show-toast-event', callbackShowToastFromEvent);

    return () => {
      window.removeEventListener('show-toast-event', callbackShowToastFromEvent);
    };
  }, []);

  useEffect(() => {
    const handleKeydown = (keyboardEvent: KeyboardEvent) => {
      switch (keyboardEvent.key.toLowerCase()) {
        case 'escape': {
          setToasts((toastsAtuais) => {
            const toastCopiado = toastsAtuais.slice();

            toastCopiado.shift();

            return toastCopiado;
          });
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {createPortal(
        <ToastsWrapper>
          {toasts.map(({ id, type, message, delay }) => (
            <Toast type={type} key={id} message={message} delay={delay} close={() => close(id)} />
          ))}
        </ToastsWrapper>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;

export const useToast = () => {
  return React.useContext(ToastContext);
};

export interface IToastContext {
  showToast(type: ToastType, message: string, delay?: number): void;
  addToast(params: { type: ToastType; description?: string }): void;
}

export interface IToastInfo {
  id: string;
  type: ToastType;
  message: string;
  delay: number;
}
